import { Map as ImmutableMap, List } from 'immutable';
import I18n from 'I18n';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { DEALS_FETCH_FAILED, DEALS_FETCH_SUCCEEDED, DEALS_FETCH_STARTED, SAVE_DEALS_UPDATES, SET_SHOW_SUBMISSION_PANEL_FROM_DEAL_TABLE, SET_SELECTED_DEAL_AMOUNT, SET_SELECTED_DEAL_COUNT, SET_SELECTED_DEAL_IDS, SET_DEAL_TABLE_PAGINATION, SET_DEAL_TABLE_SORT } from './ActionTypes';
import { updateDeals, fetchDealsRequest } from '../api/dealsApi';
import { CRM_SEARCH_DELAY, defaultDealSearchOptions } from '../constants/CrmSearchConstants';
import { ForecastAmountSettingOptions } from '../constants/PortalConfigConstants';
import { getAggregatedDealRollups } from './AggregatedOverviewDataActions';
import { dealsSelector, getDealTablePagination, getDealTableSort, getIsDefaultSort } from '../selectors/dealsSelectors';
import { getFilters, getForecastTypeFilter, getTimePeriodFilter, getDealGroupingTypeFilter } from '../selectors/filtersSelectors';
import { getIsDealStageView, getPortalConfig } from '../selectors/forecastSelectors';
import { getStagedDealsUpdates } from '../selectors/stagedDealsUpdatesSelectors';
import { getCompanyCurrency } from '../selectors/multiCurrencySelectors';
import { getDealSidebarId, getDealSidebarIsForTeam } from '../selectors/overviewSelectors';
import { trackInlineEditDealProperty } from '../utils/usageTracker';
import { isTimePeriodCustom } from '../utils/dateUtils';
import { makeForecastSearchQuery } from '../utils/filters';
import { derivePropertyKeys } from '../utils/stagedDealsUpdatesUtils';
import { transformDealsSearchPayloadToUseCurrency } from '../utils/aggregatedOverviewDataUtils';
import { createDealObjects } from '../utils/dealBuilder.ts';
import { getIsUngatedToForecastingMultipleOutcomes, getIsUngatedToManualSubmissionOverhaul } from '../selectors/authSelectors';
import { logRavenException } from 'ui-deal-management-components-lib/utils/ravenWrapperUtil';
import { convertPaginationStateForApi } from '../utils/deals';
import { convertSortStateForApi } from '../utils/deals';
export function fetchDealData(teamOrOwnerId, options) {
  return (dispatch, getState) => {
    const state = getState();
    const companyCurrency = getCompanyCurrency(state);
    const filters = getFilters(state);
    const timePeriod = getTimePeriodFilter(state);
    const dealGroupingType = getDealGroupingTypeFilter(state);
    const portalConfig = getPortalConfig(state);
    const isUngatedToForecastingMultipleOutcomes = getIsUngatedToForecastingMultipleOutcomes(state);
    const isUngatedToManualSubmissionOverhaul = getIsUngatedToManualSubmissionOverhaul(state);
    const forecastTypeId = getForecastTypeFilter(state);
    let isTeam = false;
    if (getDealSidebarId(state)) {
      isTeam = !!getDealSidebarIsForTeam(state);
    }
    const isCustomDate = isTimePeriodCustom(timePeriod);
    options = options || defaultDealSearchOptions(dealGroupingType);
    const data = makeForecastSearchQuery({
      dealGroupingType,
      filters,
      isCustomDate,
      isTeam,
      options,
      teamOrOwnerId: Number(teamOrOwnerId),
      timePeriod,
      forecastTypeId: isUngatedToForecastingMultipleOutcomes ? forecastTypeId : undefined
    });
    const queryForTotalDealAmount = makeForecastSearchQuery({
      dealGroupingType,
      filters,
      isCustomDate,
      isTeam,
      options,
      teamOrOwnerId: Number(teamOrOwnerId),
      timePeriod,
      totalDealAmountOption: 'TOTAL',
      forecastTypeId: isUngatedToForecastingMultipleOutcomes ? forecastTypeId : undefined
    });
    const dealsPromise = fetchDealsRequest(data);
    // The total deal amount is fetched *only* to populate the bulk actions label when all deals are selected
    // - If the manual submission overhaul gate is on, bulk actions do not exist
    // - Therefore, we only fetch if the user is gated to manual submission overhaul
    // - The totalOrWeightedDealAmount is used for the deal sidebar footer
    const totalAmountDealsPromise = portalConfig === ForecastAmountSettingOptions.WEIGHTED_AMOUNT && !isUngatedToManualSubmissionOverhaul ? fetchDealsRequest(queryForTotalDealAmount) : undefined;
    return Promise.all([dealsPromise, totalAmountDealsPromise]).then(([dealData, totalAmountDealData]) => {
      const deals = createDealObjects(dealData.results);
      dispatch({
        type: DEALS_FETCH_SUCCEEDED,
        payload: Object.assign({}, transformDealsSearchPayloadToUseCurrency(dealData, companyCurrency, totalAmountDealData || dealData), {
          deals
        })
      });
    }).catch(error => {
      logRavenException(error, 'Deal fetch failed', data);
      FloatingAlertStore.addAlert({
        message: I18n.text('dealTable.dealFetchFailed.message'),
        titleText: I18n.text('dealTable.dealFetchFailed.title'),
        type: 'danger'
      });
      dispatch({
        type: DEALS_FETCH_FAILED
      });
    });
  };
}
export function fetchDeals(id, options) {
  return dispatch => {
    dispatch({
      type: DEALS_FETCH_STARTED
    });
    return dispatch(fetchDealData(id, options));
  };
}
export function refetchDealData(ownerId, options) {
  return dispatch => {
    dispatch(fetchDealData(ownerId, options)).then(() => {
      setTimeout(() => {
        return dispatch(getAggregatedDealRollups());
      }, CRM_SEARCH_DELAY);
    }).catch(() => {
      // should be caught in fetchDealData
    });
  };
}
export function saveDealsUpdates(ownerId, stagedUpdatesCount) {
  return (dispatch, getState) => {
    const state = getState();
    const pagination = getDealTablePagination(state);
    const sort = getDealTableSort(state);
    const isDefaultSort = getIsDefaultSort(state);
    const stagedDealsUpdates = getStagedDealsUpdates(state);
    const deals = dealsSelector(getState());
    const data = Object.entries(stagedDealsUpdates).reduce((_data, [dealId, _properties]) => {
      const propertyEntries = Object.entries(_properties);
      const properties = List(propertyEntries.map(([property, value]) => ImmutableMap({
        name: property,
        value
      })));
      return _data.push({
        properties,
        dealId
      });
    }, List());
    updateDeals(data).then(() => {
      const propertyKeys = derivePropertyKeys(stagedDealsUpdates);
      dispatch({
        type: SAVE_DEALS_UPDATES,
        payload: {
          deals,
          dealsUpdates: stagedDealsUpdates
        }
      });
      dispatch(refetchDealData(ownerId, Object.assign({}, convertPaginationStateForApi(pagination), {
        sorts: convertSortStateForApi(sort, isDefaultSort)
      })));
      trackInlineEditDealProperty(getIsDealStageView(getState()), propertyKeys);
      FloatingAlertStore.addAlert({
        message: I18n.text('dealTable.saveBar.successBarMessage', {
          count: stagedUpdatesCount
        }),
        titleText: I18n.text('dealTable.saveBar.successBarTitle'),
        type: 'success'
      });
    }).catch(error => {
      logRavenException(error, 'Save deals updates', data);
      FloatingAlertStore.addAlert({
        message: I18n.text('dealTable.permissionsErrorMessage.message'),
        titleText: I18n.text('dealTable.permissionsErrorMessage.title'),
        type: 'danger'
      });
      dispatch(fetchDealData(ownerId, Object.assign({}, convertPaginationStateForApi(pagination), {
        sorts: convertSortStateForApi(sort, isDefaultSort)
      }))).catch(() => {
        // should be caught in fetchDealData
      });
    });
  };
}
export function setSelectedDealAmount(selectedDealAmount) {
  return dispatch => {
    dispatch({
      type: SET_SELECTED_DEAL_AMOUNT,
      payload: selectedDealAmount
    });
  };
}
export function setSelectedDealCount(selectedDealCount) {
  return dispatch => {
    dispatch({
      type: SET_SELECTED_DEAL_COUNT,
      payload: selectedDealCount
    });
  };
}
export function setSelectedDealIds(dealIds) {
  return (dispatch, getState) => {
    const forecastTypeId = getForecastTypeFilter(getState());
    dispatch({
      type: SET_SELECTED_DEAL_IDS,
      payload: {
        forecastTypeId: forecastTypeId !== null && forecastTypeId !== void 0 ? forecastTypeId : 0,
        dealIds
      }
    });
  };
}
export function setShowSubmissionPanelFromDealTable(shouldShowSubmissionPanel) {
  return dispatch => {
    dispatch({
      type: SET_SHOW_SUBMISSION_PANEL_FROM_DEAL_TABLE,
      payload: shouldShowSubmissionPanel
    });
  };
}
export function setDealTablePagination(pagination) {
  return {
    type: SET_DEAL_TABLE_PAGINATION,
    payload: pagination
  };
}
export function setDealTableSort(sort, isDefault) {
  return {
    type: SET_DEAL_TABLE_SORT,
    payload: {
      state: sort,
      isDefault
    }
  };
}